"use client";

import * as React from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { RANGES } from "@/components/time-range-selector";
import type { LoginTimeBucket } from "@/providers/admin.provider";

const chartConfig = {
  count: {
    label: "Logins",
    color: "var(--primary)",
  },
} satisfies ChartConfig;

// Add `minutes` to a "HH:MM" bucket key, wrapping across midnight.
function addMinutes(bucket: string, minutes: number) {
  const [h, m] = bucket.split(":").map(Number);
  const total = (h * 60 + m + minutes + 24 * 60) % (24 * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// Fill in every 5-minute slot of the day (288 total) so the chart always
// spans the full 00:00–24:00 range, even where the source is sparse.
// `bucket` stays a string — recharts/shadcn's ChartTooltipContent only reads
// the axis value out of `label` when it's typed as a string; a numeric
// dataKey falls back to the series label instead (rendering NaN).
function fillFiveMinuteBuckets(buckets: LoginTimeBucket[]) {
  const byBucket = new Map(buckets.map((b) => [b.bucket, b.count]));
  const filled: { bucket: string; count: number }[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      const key = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      filled.push({ bucket: key, count: byBucket.get(key) ?? 0 });
    }
  }

  return filled;
}

interface Props {
  data: LoginTimeBucket[];
  // Look-back window the data covers, for the description. The per-user
  // local_time source has no `days` filter, so it leaves this unset.
  days?: number;
  isLoading: boolean;
  // Label shown in the title/tooltip, e.g. "user time" or "UTC".
  timeLabel?: string;
}

export function ChartUserLoginTimes({
  data,
  days,
  isLoading,
  timeLabel = "user time",
}: Props) {
  const bucketData = React.useMemo(() => fillFiveMinuteBuckets(data), [data]);

  const totalLogins = React.useMemo(
    () => data.reduce((sum, d) => sum + d.count, 0),
    [data],
  );

  const rangeLabel =
    days !== undefined
      ? (RANGES.find((r) => r.value === String(days))?.label ?? `${days} days`)
      : undefined;

  return (
    <Card className="@container/card">
      <CardHeader>
        <div className="flex min-w-0 flex-col gap-1">
          <CardTitle>Login times ({timeLabel})</CardTitle>
          <CardDescription>
            {isLoading
              ? "Loading…"
              : rangeLabel
                ? `${totalLogins.toLocaleString()} logins — ${rangeLabel.toLowerCase()}`
                : `${totalLogins.toLocaleString()} logins`}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {isLoading ? (
          <Skeleton className="aspect-auto h-[200px] w-full rounded-lg" />
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[200px] w-full"
          >
            <BarChart data={bucketData}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="bucket"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={11}
                tickFormatter={(bucket) => `${Number(String(bucket).split(":")[0])}h`}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
                width={32}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(bucket) =>
                      `${bucket}–${addMinutes(String(bucket), 5)} (${timeLabel})`
                    }
                    indicator="dot"
                  />
                }
              />
              <Bar dataKey="count" fill="var(--color-count)" radius={2} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
